import { useState, useEffect } from "react";
import { Brain, Sparkles, FileText, Copy, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { getAIInsightApi, reportRiskApi } from "../../../services/axios/AuthApis/RisksManagementApis";
import ApiLoader from "../../ui/ApiLoader";

const AI_FEATURES = [
    {
        id: "risk_statement_generation",
        name: "Risk Statement Generation",
        description: "Generate compliant risk statements",
        icon: FileText,
        color: "pink",
        placeholder: "Describe the risk scenario for which you need a formal risk statement..."
    },
    {
        id: "treatment_recommendation",
        name: "Treatment Recommendation",
        description: "Get AI-powered treatment suggestions",
        icon: Sparkles,
        color: "amber",
        placeholder: "Describe the risk for which you need treatment recommendations..."
    },
    {
        id: "duplicate_risk_detection",
        name: "Duplicate Risk Detection",
        description: "Identify potentially duplicate risks",
        icon: AlertTriangle,
        color: "red",
        placeholder: "Paste risk descriptions to analyze for duplicates..."
    },
    {
        id: "executive_summary",
        name: "Executive Summary",
        description: "Generate executive-level risk summary",
        icon: Brain,
        color: "green",
        placeholder: "This will generate a summary based on your current risk data..."
    }
];

export const AIInsights = (userId) => {
    const [selectedFeature, setSelectedFeature] = useState(AI_FEATURES[0]);
    const [context, setContext] = useState("");
    const [selectedRisk, setSelectedRisk] = useState("");
    const [risks, setRisks] = useState([]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingRisks, setLoadingRisks] = useState(true);

    useEffect(() => {
        fetchRisks();
    }, []);

    const fetchRisks = async () => {
        try {
            const res = await reportRiskApi("risk-register", userId.userId);
            if (res.success) {
                setRisks(res.data);
            }
        } catch (err) {
            console.error("Failed to load risks");
        } finally {
            setLoadingRisks(false);
        }
    };

    const generateInsight = async () => {
        setLoading(true);
        setResult(null);

        try {
            const payload = {
                feature: selectedFeature.id,
                context: context || undefined,
                risk_id: selectedRisk || undefined
            }
            const res = await getAIInsightApi(payload);
            if (res.success) {
                setResult(res.data);
                console.log("AI insight generated");
            }
        } catch (err) {
            console.error(err.response?.data?.detail || "Failed to generate insight");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (result?.result) {
            navigator.clipboard.writeText(result.result);
            console.log("Copied to clipboard");
        }
    };

    const colorClasses = {
        pink: "bg-pink-50 border-pink-200 text-pink-600",
        amber: "bg-amber-50 border-amber-200 text-amber-600",
        red: "bg-red-50 border-red-200 text-red-600",
        green: "bg-green-50 border-green-200 text-green-600",
    };

    if (loadingRisks) {
        return <ApiLoader label="Loading AI Insights…" className="py-20" />;
    }

    return (
        <div className="space-y-6" data-testid="ai-insights-tab">
            {/* AI Constraints Notice */}
            <Card className="card-surface border-amber-200 bg-amber-50">
                <CardContent className="p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-amber-800">AI Governance</p>
                        <p className="text-xs text-amber-700 mt-1">
                            AI outputs are aligned with risk management best practices. All recommendations should be reviewed by the Risk Governance Committee.
                        </p>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Feature Selection */}
                <div className="lg:col-span-1 space-y-3">
                    <h3 className="text-sm font-medium text-slate-700">Select AI Feature</h3>
                    {AI_FEATURES.map(feature => {
                        const Icon = feature.icon;
                        const isSelected = selectedFeature.id === feature.id;
                        return (
                            <button
                                key={feature.id}
                                onClick={() => {
                                    setSelectedFeature(feature);
                                    setResult(null);
                                    setContext("");
                                    setSelectedRisk("");
                                }}
                                className={`w-full p-4 rounded-lg border text-left transition-all ${isSelected
                                    ? `${colorClasses[feature.color]} ring-2 ring-${feature.color}-300`
                                    : "bg-white border-slate-200 hover:border-slate-300"
                                    }`}
                                data-testid={`ai-feature-${feature.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded ${isSelected ? "" : "bg-slate-100"}`}>
                                        <Icon className={`w-4 h-4 ${isSelected ? "" : "text-slate-500"}`} />
                                    </div>
                                    <div>
                                        <p className={`font-medium ${isSelected ? "" : "text-slate-800"}`}>
                                            {feature.name}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">{feature.description}</p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Input & Output */}
                <div className="lg:col-span-2 space-y-4">
                    <Card className="card-surface">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                {(() => {
                                    const Icon = selectedFeature.icon;
                                    return <Icon className="w-5 h-5 text-pink-500" />;
                                })()}
                                {selectedFeature.name}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Risk Selection */}
                            {(selectedFeature.id === "risk_statement_generation" ||
                                selectedFeature.id === "treatment_recommendation") && (
                                    <div>
                                        <label className="text-sm text-slate-600">Select existing risk (optional)</label>
                                        <Select value={selectedRisk || "none"} onValueChange={(val) => setSelectedRisk(val === "none" ? "" : val)}>
                                            <SelectTrigger className="mt-1 bg-white border-slate-300" data-testid="risk-select">
                                                <SelectValue placeholder="Select a risk for context" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                <SelectItem value="none">None</SelectItem>
                                                {risks.slice(0, 20).map(risk => (
                                                    <SelectItem key={risk.id} value={risk.id}>
                                                        {risk.risk_id} - {risk.risk_description.substring(0, 40)}...
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                            {/* Context Input */}
                            {selectedFeature.id !== "executive_summary" && (
                                <div>
                                    <label className="text-sm text-slate-600">Additional context</label>
                                    <Textarea
                                        value={context}
                                        onChange={(e) => setContext(e.target.value)}
                                        placeholder={selectedFeature.placeholder}
                                        className="mt-1 min-h-[120px] bg-white border-slate-300"
                                        data-testid="ai-context-input"
                                    />
                                </div>
                            )}

                            {selectedFeature.id === "executive_summary" && (
                                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                                    <p className="text-sm text-slate-600">
                                        This feature will analyze your current risk data and generate an executive summary
                                        suitable for C-level leadership. Click "Generate" to proceed.
                                    </p>
                                </div>
                            )}

                            <Button
                                onClick={generateInsight}
                                disabled={loading}
                                className="w-full btn-primary"
                                data-testid="generate-insight-btn"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Generating...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <Sparkles className="w-4 h-4" />
                                        Generate Insight
                                    </span>
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Result */}
                    {result && (
                        <Card className="card-surface bg-gradient-to-br from-pink-50 to-purple-50 border-pink-200" data-testid="ai-result">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                        <Sparkles className="w-5 h-5 text-pink-500" />
                                        AI Response
                                    </CardTitle>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={copyToClipboard}
                                            className="text-slate-500 hover:text-slate-700"
                                            data-testid="copy-result-btn"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={generateInsight}
                                            disabled={loading}
                                            className="text-slate-500 hover:text-slate-700"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500">
                                    Generated: {new Date(result.generated_at).toLocaleString()}
                                </p>
                            </CardHeader>
                            <CardContent>
                                <div className="prose prose-sm max-w-none">
                                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                                        {result.result}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};
